/**
 * useKeyboardNavigation Hook
 *
 * A comprehensive React hook for implementing keyboard navigation in dropdown menus,
 * lists, and hierarchical components. Supports both flat and hierarchical navigation patterns.
 *
 * @param config - Configuration object containing navigation settings
 * @param config.isOpen - Boolean indicating if the component is open/active
 * @param config.items - Array of navigation items with optional children for hierarchy
 * @param config.onItemSelect - Callback when an item is selected (Enter/Space)
 * @param config.onClose - Callback when navigation should close (Escape)
 * @param config.containerRef - Ref to the container element for focus management
 * @param config.hierarchical - Enable hierarchical navigation (default: false)
 *
 * @returns Object containing current focus state and navigation path
 * @returns focusedIndex - Currently focused item index
 * @returns navigationPath - Array representing current position in hierarchy
 *
 * @example Flat Navigation
 * ```tsx
 * import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
 *
 * const MyList = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   const items = [
 *     { id: '1', label: 'Item 1' },
 *     { id: '2', label: 'Item 2' }
 *   ];
 *
 *   const { focusedIndex } = useKeyboardNavigation({
 *     isOpen,
 *     items,
 *     onItemSelect: (item, index) => console.log('Selected:', item),
 *     onClose: () => setIsOpen(false),
 *     containerRef
 *   });
 *
 *   return (
 *     <div ref={containerRef}>
 *       {items.map((item, index) => (
 *         <div key={item.id} className={index === focusedIndex ? 'focused' : ''}>
 *           {item.label}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * };
 * ```
 *
 * @example Hierarchical Navigation (Menus with Submenus)
 * ```tsx
 * const MyMenu = () => {
 *   const items = [
 *     {
 *       id: 'file',
 *       label: 'File',
 *       children: [
 *         { id: 'new', label: 'New' },
 *         { id: 'open', label: 'Open' }
 *       ]
 *     }
 *   ];
 *
 *   const { focusedIndex, navigationPath } = useKeyboardNavigation({
 *     isOpen: true,
 *     items,
 *     onItemSelect: (item, index, path) => console.log('Selected:', item),
 *     onClose: () => setIsOpen(false),
 *     containerRef,
 *     hierarchical: true
 *   });
 *
 *   // navigationPath[0] = main menu index, navigationPath[1] = submenu index
 * };
 * ```
 *
 * @keyboardControls
 * - ArrowUp/ArrowDown: Navigate through items
 * - ArrowRight: Enter submenu (hierarchical mode)
 * - ArrowLeft: Exit submenu (hierarchical mode)
 * - Enter/Space: Select current item
 * - Escape: Close menu or exit current submenu level
 *
 * @features
 * - Supports both flat and hierarchical navigation
 * - Automatic focus management with visual indicators
 * - Configurable item selection callbacks with context
 * - Memory leak prevention with memoized event handlers
 * - Accessible keyboard interaction patterns
 * - Works with any data structure via generic types
 */
import { useState, useEffect, RefObject, useCallback, useMemo } from 'react';

interface NavigationItem {
  id: string;
  value?: string;
  label?: string;
  children?: NavigationItem[];
}

interface HierarchicalNavigationConfig<T extends NavigationItem> {
  isOpen: boolean;
  items: T[];
  onItemSelect: (item: T, index: number, path: number[]) => void;
  onClose: () => void;
  containerRef?: RefObject<HTMLElement>;
  onEnterSubmenu?: (path: number[]) => void;
  onExitSubmenu?: (path: number[]) => void;
  hierarchical?: true;
}

interface FlatNavigationConfig<T extends NavigationItem> {
  isOpen: boolean;
  items: T[];
  onItemSelect: (item: T, index: number) => void;
  onClose: () => void;
  containerRef?: RefObject<HTMLElement>;
  hierarchical?: false;
}

type KeyboardNavigationConfig<T extends NavigationItem> =
  | HierarchicalNavigationConfig<T>
  | FlatNavigationConfig<T>;

export const useKeyboardNavigation = <T extends NavigationItem>(
  config: KeyboardNavigationConfig<T>,
) => {
  const { isOpen, items, onItemSelect, onClose, containerRef } = config;
  const hierarchical = config.hierarchical || false;

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [navigationPath, setNavigationPath] = useState<number[]>([]);

  // Reset focus state when items change
  const itemsString = useMemo(() => JSON.stringify(items), [items]);
  useEffect(() => {
    setFocusedIndex(-1);
    setNavigationPath([]);
  }, [itemsString]);

  const getCurrentItems = useCallback((): T[] => {
    if (!hierarchical) return items;

    let currentItems = items;
    for (const pathIndex of navigationPath) {
      if (currentItems[pathIndex]?.children) {
        currentItems = currentItems[pathIndex].children as T[];
      }
    }
    return currentItems;
  }, [hierarchical, items, navigationPath]);

  const getCurrentItem = useCallback(() => {
    const currentItems = getCurrentItems();
    return currentItems[focusedIndex];
  }, [getCurrentItems, focusedIndex]);

  // Memoize navigation path string to prevent unnecessary re-renders
  const navigationPathString = useMemo(() => navigationPath.join(','), [navigationPath]);

  // Memoize the keydown handler to prevent memory leaks
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      const currentItems = getCurrentItems();

      switch (event.key) {
        case 'Tab':
          event.preventDefault();
          onClose();
          setFocusedIndex(-1);
          containerRef?.current?.blur();
          break;
        case 'Escape':
          event.preventDefault();
          if (navigationPath.length > 0) {
            // Close current submenu and return to parent level
            const parentIndex = navigationPath[navigationPath.length - 1] || 0;
            setNavigationPath((prev) => prev.slice(0, -1));
            setFocusedIndex(parentIndex);
          } else {
            // Close entire menu if at root level
            onClose();
            setFocusedIndex(-1);
            containerRef?.current?.blur();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            if (currentItems.length === 0) return -1;
            if (prev === -1) return 0;
            return prev >= currentItems.length - 1 ? currentItems.length - 1 : prev + 1;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            if (currentItems.length === 0) return -1;
            if (prev <= 0) return 0;
            return prev - 1;
          });
          break;
        case 'ArrowRight':
          if (hierarchical && focusedIndex >= 0) {
            event.preventDefault();
            const currentItem = getCurrentItem();
            if (currentItem?.children?.length) {
              const newPath = [...navigationPath, focusedIndex];
              setNavigationPath(newPath);
              setFocusedIndex(0);
              (config as HierarchicalNavigationConfig<T>).onEnterSubmenu?.(newPath);
            }
          }
          break;
        case 'ArrowLeft':
          if (hierarchical && navigationPath.length > 0) {
            event.preventDefault();
            const newPath = navigationPath.slice(0, -1);
            const parentIndex = navigationPath[navigationPath.length - 1];
            setNavigationPath(newPath);
            setFocusedIndex(parentIndex);
            (config as HierarchicalNavigationConfig<T>).onExitSubmenu?.(newPath);
          }
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(currentItems.length > 0 ? currentItems.length - 1 : -1);
          break;
        case 'Enter':
        case ' ':
          // Don't prevent default for space if user is typing in an input field
          if (event.key === ' ' && event.target instanceof HTMLInputElement) {
            return;
          }
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < currentItems.length) {
            const currentItem = currentItems[focusedIndex];
            if (hierarchical && currentItem?.children?.length) {
              // Enter opens submenu if item has children
              const newPath = [...navigationPath, focusedIndex];
              setNavigationPath(newPath);
              setFocusedIndex(0);
              (config as HierarchicalNavigationConfig<T>).onEnterSubmenu?.(newPath);
            } else {
              // Enter selects item if no children
              if (hierarchical) {
                (config as HierarchicalNavigationConfig<T>).onItemSelect(
                  currentItem,
                  focusedIndex,
                  navigationPath,
                );
              } else {
                (config as FlatNavigationConfig<T>).onItemSelect(currentItem, focusedIndex);
              }
            }
          }
          break;
      }
    },
    [isOpen, focusedIndex, navigationPathString, hierarchical, onClose, onItemSelect, items],
  );

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, containerRef]);

  return {
    focusedIndex,
    setFocusedIndex,
    navigationPath,
    setNavigationPath,
    getCurrentItems,
    getCurrentItem,
  };
};
